import { onlineManager } from '@tanstack/react-query';
import { AxiosRequestConfig } from 'axios';
import LangEnum from '../../../lang/LangEnum';
import { TeceoApiResponse } from '../../../lib/basicRepository';
import { BRAND_ID_HEADER_KEY, restApi } from '../../../services/api';
import downloadMediasNotifierStore from '../../../stores/DownloadMediasNotifier.store';
import snackbarStore from '../../../stores/Snackbar.store';
import { createLinkToDownloadBlob } from '../../../utils/createLinkToDownload';
import { orderActionsRepositoryOfflineAdapters } from '../../offline/offlineRequestAdapters/orderActions.repository.offlineAdapters';
import { TranslateType } from '../../translation/translationContext.types';
import { downloadMediasFrame } from '../dowload-medias/downloadMediasFrame';
import { ExportOrderEventCsvParams } from '../order.types';
import { OrderActionsRepositoryParams } from './orderActions.repository.types';
import { exportOrderCsv } from './ordersRepository/onlineOrdersRepository';

const { handleMessage } = snackbarStore;

const handleDownloadMedias = async (
  id: string,
  code: string,
  brandCommercialName: string,
  brandId?: string,
) => {
  let axiosConfig: AxiosRequestConfig = {};
  if (brandId) {
    axiosConfig = { headers: { [BRAND_ID_HEADER_KEY]: brandId } };
  }

  const { addOrder } = downloadMediasNotifierStore;
  const response: TeceoApiResponse<string> = await restApi.get(
    `orders/${id}/order-products/download-images`,
    {},
    axiosConfig,
  );

  if (response.ok) {
    if (response.data) {
      downloadMediasFrame(response.data);
    } else {
      addOrder({ id, code, brandCommercialName });
    }

    return;
  }

  handleMessage({ type: 'error', message: response.data?.message });
};

const handleSplitOrder = async (id: string) => {
  const response: TeceoApiResponse<void> = await restApi.post(`orders/${id}/split`);

  if (!response.ok) {
    handleMessage({ type: 'error', message: response.data?.message });
  }

  return response.ok;
};

const handleSync = async (id: string, translate: TranslateType, brandId?: string) => {
  let axiosConfig: AxiosRequestConfig = {};
  if (brandId) {
    axiosConfig = { headers: { [BRAND_ID_HEADER_KEY]: brandId } };
  }

  const response: any = await restApi.post(`orders/${id}/force-sync`, {}, axiosConfig);
  if (response.ok) {
    handleMessage({
      type: 'info',
      message: translate(LangEnum.SNACKBAR_REQUEST_SENT_FOR_SYNC),
    });
  } else {
    handleMessage({
      type: 'error',
      message: response.data.message ?? translate(LangEnum.SNACKBAR_ERROR_SYNCHRONIZING_ORDER),
    });
  }
  return response.ok;
};

const BLOB_AXIOS_CONFIG: AxiosRequestConfig = {
  responseType: 'blob',
};

export const exportOrderIntegrationCsv = async ({
  eventId,
  filename,
}: ExportOrderEventCsvParams) => {
  const response = await restApi.get<Blob>(
    `/order-integration-result/${eventId}/csv`,
    {},
    BLOB_AXIOS_CONFIG,
  );

  if (response.ok && response.data) {
    createLinkToDownloadBlob({ data: response.data, filename, archiveType: 'csv' });
  }

  return response;
};

export const exportOrderHistoricStatusCsv = async ({
  eventId,
  filename,
}: ExportOrderEventCsvParams) => {
  const response = await restApi.get<Blob>(
    `/orders-export/order-history/${eventId}/csv`,
    {},
    BLOB_AXIOS_CONFIG,
  );

  if (response.ok && response.data) {
    createLinkToDownloadBlob({ data: response.data, filename, archiveType: 'csv' });
  }

  return response;
};

export const onlineOrderActionsRepository = ({
  orderId,
  translate,
  brandId,
}: OrderActionsRepositoryParams) => {
  const exportCsv = async (filename: string) => {
    exportOrderCsv({
      orderId,
      filename,
      translate,
      brandId,
    });
  };

  const sync = async () => handleSync(orderId, translate, brandId);

  const downloadMedias = async (code: string, commercialName: string) =>
    handleDownloadMedias(orderId, code, commercialName, brandId);

  const splitOrder = async () => handleSplitOrder(orderId);

  return {
    splitOrder,
    exportCsv,
    sync,
    downloadMedias,
  };
};

export default ({ isOfflineMode, ...params }: OrderActionsRepositoryParams) =>
  isOfflineMode || !onlineManager.isOnline()
    ? orderActionsRepositoryOfflineAdapters(params)
    : onlineOrderActionsRepository(params);
